import { isSpecialRiftTurn, pickUniformRandom } from '@prometheus/utils'
import translateOrbitingPlanet from '../../utils/translateOrbitingPlanet'

/**
 * The Ready button's gate: what (if anything) the game is still waiting on
 * from this player before "Ready" can mean "Ready".
 *
 * Ready used to dead-end in two phases. During travel the server refuses it
 * ("There are still players who need to pick which planet they want to move
 * to") and `PromiseButtonV2` greys every button in the game; during a trade the
 * header button is disabled outright. Both times the actual next step lived
 * somewhere else on screen — a planet card, the Galactic Trader — which on a
 * phone is a tab away. So a tap that should have ended the turn did nothing,
 * or threw.
 *
 * Now the same tap ANSWERS the requirement: the first tap reveals the options
 * under the button, the second lets the game choose one. This module decides
 * which requirement is live and what the choices are; the hook
 * (`hooks/useReadyGate`) runs them and the overlay draws them. Pure so the
 * rules can be tested without a store or a DOM.
 */

export type ReadyRequirementKind =
  /** This player must pick a travel destination, and can pick it right now. */
  | 'travel'
  /** A trade is open and this player has not committed an offer yet. */
  | 'trade'
  /** Travel is blocked on somebody else (or on a rift queue) — nothing to decide. */
  | 'waiting-travel'
  /** Offer committed; the trade is waiting on the other players. */
  | 'waiting-trade'

export interface TravelChoice {
  /** The `planet_movement_options` key — exactly what `pickChoosablePlanet` takes. */
  key: string
  /** Planet name, e.g. "Portia". */
  name: string
  /** Orbit letter, e.g. "U". */
  orbit: string
  /** Orbit spelled out, e.g. "Uranus". */
  orbitName: string
}

export interface ReadyRequirement {
  kind: ReadyRequirementKind
  title: string
  hint: string
  /** Travel destinations to offer; empty for every other kind. */
  choices: TravelChoice[]
  /**
   * What a second tap on Ready does, phrased for the button ("travel to a
   * random planet"). `null` when there is nothing this player can decide —
   * the panel then just explains who is being waited on.
   */
  autoLabel: string | null
}

export interface ReadyGateState {
  uid: string
  dead: boolean
  turn: number
  /** `metadata.players_choosing_planet`, in pick order. */
  playersChoosingPlanet: string[]
  /** uid -> display name, for the waiting message. */
  playerNames: Record<string, string>
  /** Keys of `sheet.planet_movement_options`. */
  movementOptions: string[]
  /**
   * Planets any ship is currently parked on (`players[*].current_planet`).
   * On a decennial rift year every other travel control forbids landing on an
   * occupied planet, so the gate's choices — and its random pick — must too.
   */
  occupiedPlanets: string[]
  /** A Galactic Trader window is open this turn. */
  tradeOpen: boolean
  /** This player's offer is already committed (`sheet.trade_offer.committed`). */
  tradeCommitted: boolean
  /** The committed-or-saved offer has no quantities on it — committing it is a skip. */
  tradeOfferEmpty: boolean
}

/**
 * Split a movement-option key ("U|N:Portia|25") into something displayable.
 * Same shape `PlayerChoosePlanetMovement` parses: `orbit|quadrant:name|data`.
 */
export function parseTravelChoice(key: string): TravelChoice {
  const [region = '', planet = ''] = key.split(':')
  const orbit = region.split('|')[0] || ''
  const name = planet.split('|')[0] || key
  return {
    key,
    name,
    orbit,
    orbitName: translateOrbitingPlanet(orbit),
  }
}

/**
 * The destinations this player may actually take. On a decennial rift year the
 * client-wide rule forbids landing on a planet another ship occupies — the full
 * card, the compact card and the board sheet all disable it — so the gate's
 * buttons, and its random pick, draw from the same reduced pool.
 */
export function legalTravelOptions(state: Pick<ReadyGateState, 'turn' | 'movementOptions' | 'occupiedPlanets'>): string[] {
  if (!isSpecialRiftTurn(state.turn)) {
    return state.movementOptions
  }
  return state.movementOptions.filter(key => !state.occupiedPlanets.includes(key))
}

/**
 * Can this player act on their travel choice this instant?
 *
 * On a special rift turn the queue is strict — only the head of
 * `players_choosing_planet` may pick, and the engine rejects anyone else — so
 * offering the rest of the queue a list of planets would just be a list of
 * errors waiting to happen.
 */
export function canPickTravelNow(state: Pick<ReadyGateState, 'uid' | 'turn' | 'playersChoosingPlanet' | 'movementOptions' | 'occupiedPlanets'>): boolean {
  if (!state.playersChoosingPlanet.includes(state.uid) || !legalTravelOptions(state).length) {
    return false
  }
  return !isSpecialRiftTurn(state.turn) || state.playersChoosingPlanet[0] === state.uid
}

const nameList = (uids: string[], names: Record<string, string>) =>
  uids.map(uid => names[uid] || 'another player').join(', ')

/**
 * What is standing between this player and a readied turn — or `null` when
 * nothing is and Ready should just fire.
 *
 * Trade is checked first: it is the phase where the button was hard-disabled,
 * and a trade never overlaps a travel queue (the engine opens the trader only
 * once every choice is in).
 */
export function resolveReadyRequirement(state: ReadyGateState): ReadyRequirement | null {
  if (state.dead) {
    return null
  }

  if (state.tradeOpen) {
    if (state.tradeCommitted) {
      return {
        kind: 'waiting-trade',
        title: 'Offer submitted',
        hint: 'The Galactic Trader is waiting on the other players.',
        choices: [],
        autoLabel: null,
      }
    }
    return {
      kind: 'trade',
      title: 'The Galactic Trader is open',
      hint: state.tradeOfferEmpty
        ? 'You have no bids in. Skip the trade to end your turn.'
        : 'Your bids are saved but not committed yet.',
      choices: [],
      autoLabel: state.tradeOfferEmpty ? 'skip the trade' : 'commit your saved bids',
    }
  }

  if (!state.playersChoosingPlanet.length) {
    return null
  }

  if (canPickTravelNow(state)) {
    const choices = legalTravelOptions(state).map(parseTravelChoice)
    return {
      kind: 'travel',
      title: 'Choose where to travel',
      hint: 'Pick a destination, or let the ship pick one.',
      choices,
      autoLabel: choices.length > 1 ? 'travel to a random destination' : 'travel there',
    }
  }

  // Somebody has to move before anyone can ready up — including, on a rift
  // turn, players ahead of this one in the queue.
  const others = state.playersChoosingPlanet.filter(id => id !== state.uid)
  const waitingOnMe = state.playersChoosingPlanet.includes(state.uid)
  // It IS this player's turn to act, but every reachable planet is occupied —
  // only possible on a rift year, at the head of the queue.
  const allDestinationsOccupied = waitingOnMe
    && state.movementOptions.length > 0
    && !legalTravelOptions(state).length
    && state.playersChoosingPlanet[0] === state.uid
  return {
    kind: 'waiting-travel',
    title: 'Waiting on travel',
    hint: waitingOnMe && !state.movementOptions.length
      ? 'You have no destinations to choose from this year — the game will move on shortly.'
      : allDestinationsOccupied
        ? 'Every destination you could take is occupied this decennial year — the game will move on shortly.'
        : waitingOnMe
          ? `It is not your turn to pick yet. Waiting for: ${nameList(state.playersChoosingPlanet.slice(0, 1), state.playerNames)}.`
          : `Waiting for: ${nameList(others, state.playerNames)} to pick their move.`,
    choices: [],
    autoLabel: null,
  }
}

/**
 * The game's own travel decision — the SAME shared rule `forceTurnEnd` applies
 * when the timer runs out on an undecided player: any legal option, uniformly
 * at random. (The gate's pool is additionally occupancy-filtered on rift years
 * via `legalTravelOptions`; the engine's timer pick is not.)
 */
export function pickAutoTravel(choices: TravelChoice[], random: () => number = Math.random): TravelChoice | undefined {
  return pickUniformRandom(choices, random)
}

/**
 * Identity of the current requirement, so the overlay can close itself when
 * the game moves on (choice made, trade committed, turn processed) instead of
 * hanging around describing a phase that has ended.
 */
export function readyRequirementSignature(requirement: ReadyRequirement | null): string {
  return requirement ? requirement.kind : 'none'
}
